import { Component, OnInit, Inject, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';

import { DatePipe } from '@angular/common';
import { NgForm } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { WorkOrder } from '../../shared/models/work.order.interface';
import { WorkOrderType } from '../../shared/models/work.order.type.interface';
import { Crew } from '../../shared/models/crew.interface';
import { Resource } from '../../shared/models/resource.interface';
import { ResourceEdit } from '../models/resource.edit.interface';
import { Customer } from '../../shared/models/customer.interface';
import { DateService } from '../../shared/services/date.service';
import { WorkOrderFilter } from '../models/work.order.filter';
import { WorkOrderService } from '../services/work.order.service';
import { FileService } from '../../shared/services/file.service';
import {RolePermission } from '../../shared/models/role.permission.enum';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AbstractBiditComponent } from '../../shared/controls/abstract.bidit.component';
import { WorkOrderDialog, WorkOrderDialogData } from '../../shared/controls/work-order-dialog/work.order.dialog';
import { LaborDialog } from '../labor-dialog/labor.dialog';
import { FieldTechnician } from '../../shared/models/field.technician.interface';
import { WorkOrderPrintDialog } from '../work-order-print-dialog/work-order-print-dialog.component';
import { JobType } from '../../shared/models/job.type.interface';

export interface DealerWorkOrderSummary {
  workOrderCount: number;
  workOrderTypes: WorkOrderType[];
  crews: Crew[];
  jobTypes: JobType[];
  customers: Customer[];
  resources: Resource[];
  fieldTechnicians: FieldTechnician[];
}

export interface WorkOrderResourceDialogData {
  workOrder: WorkOrder;

  resourceTypes: ResourceTypeEdit[];
}

export interface ResourceTypeEdit {
  resourceType_Desc: string,

  resources: ResourceEdit[];
}

@Component({
  selector: 'app-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss']
})
export class ListComponent extends AbstractBiditComponent {
  
  workOrders: WorkOrder[];

  summary: DealerWorkOrderSummary;

  filter: WorkOrderFilter = { jobTypeId: null, crew: '' as any } as WorkOrderFilter;

  canEdit: boolean;
  
  times: string[];
  
  constructor(private datepipe: DatePipe, private dialog: MatDialog,
    private fileService: FileService, private dateService: DateService, protected workOrderService: WorkOrderService,
    deviceDetectorService: DeviceDetectorService) {

    super(deviceDetectorService);

    let today = new Date();
    this.filter.startDate = new Date(today);
    this.filter.startDate.setDate(today.getDate() - today.getDay());

    this.filter.endDate = new Date(this.filter.startDate);
    this.filter.endDate.setDate(this.filter.startDate.getDate() + 6);
  }

  init() {
    this.canEdit = WorkOrderService.hasPermission(RolePermission.EditWorkOrder);

    this.times = new Array();
    for (let hour = 6; hour < 22; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        let timeValue = new Date(1970, 0, 1, hour, minute, 0);
        this.times.push(this.datepipe.transform(timeValue, 'hh:mm a'));
      }
    }
    this.times.push('10:00 PM');

    this.loadSummary();
  }

  loadSummary() {
    this.workOrderService.getSummary(this.filter)
      .subscribe({
        next: (data) => {
          let crewId = this.filter.crew?.crew_ID;
          this.summary = data;

          if (crewId != null)
            this.filter.crew = this.summary.crews.find(c => c.crew_ID == crewId);
        },
        error: (error: string) => this.errors = error
      });
    
  }

  loadWorkOrders() {
  }

  editWorkOrder(workOrder: WorkOrder) {
    let dialogData = {
      quoteId: workOrder.quote_ID,
      workOrderId: workOrder.workOrder_ID,

      times: this.times,
      workOrderTypes: this.summary.workOrderTypes,
      crews: this.summary.crews
    } as WorkOrderDialogData;

    const dialogRef = this.dialog.open(WorkOrderDialog, {
      width: '700px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        this.loadWorkOrders();
      }
    });
  }

  workOrderResources(workOrder: WorkOrder) {
    let resourceTypes = this.workOrderService.parseResourceTypes(workOrder, this.summary.resources);

    const dialogRef = this.dialog.open(WorkOrderResourceDialog, {
      width: '750px',
      data: {
        workOrder: workOrder,
        resourceTypes: resourceTypes
      } as WorkOrderResourceDialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        this.loadWorkOrders();
      }
    });

  }

  workOrderLabor(workOrder: WorkOrder) {
    const dialogRef = this.dialog.open(LaborDialog, {
      width: '850px',
      data: {
        workOrder: workOrder,
        crews: this.summary.crews,
        fieldTechnicians: this.summary.fieldTechnicians
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        this.loadWorkOrders();
      }
    });

  }

  printWorkOrder(workOrder: WorkOrder) {
    const dialogRef = this.dialog.open(WorkOrderPrintDialog, {
      width: '850px',
      data: {
        workOrderId: workOrder.workOrder_ID,
        showPrintAttachments: true
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
    });
  }

  closeWorkOrder(workOrder: WorkOrder) {
    this.editWorkOrder(workOrder);
  }
}

@Component({
  selector: 'work.order.resource.dialog',
  templateUrl: 'work.order.resource.dialog.html',
})
export class WorkOrderResourceDialog extends AbstractBiditComponent {

  constructor(
    deviceDetectorService: DeviceDetectorService,
    private workOrderService: WorkOrderService,
    public dialogRef: MatDialogRef<WorkOrderResourceDialog>,
    @Inject(MAT_DIALOG_DATA) public data: WorkOrderResourceDialogData) {
    super(deviceDetectorService);
  }

  updateResource(form: NgForm): void {
    if (form.valid) {
      this.isRequesting = true;

      let selections: ResourceEdit[] = new Array();
      this.data.resourceTypes.forEach(function (resourceType) {
        selections = selections.concat(resourceType.resources.filter((r) => r.isSelected));
      });

      this.workOrderService.saveResources(this.data.workOrder.workOrder_ID, selections)
        .subscribe(
          {
            next: (data) => this.dialogRef.close(data),
            error: (error: string) => this.onError(error)
          });

    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

}
